// 地图编辑视口状态仅用于显示/定位/平移，不写入地图草稿或运行快照。
export default {
  data () {
    return { showCellLabels: true, cellLookup: '', mapLookupError: '', mapPanMode: false, mapPanX: 0, mapPanY: 0, mapPanBlockUntil: 0,
      mapPanning: false, mapConfigCollapsed: false, mapExpanded: false, mapViewportWidth: 0, mapViewportHeight: 0 }
  },
  computed: {
    mapSvgStyle () { return { width: (this.mapZoom * 100) + '%', height: 'auto' } },
    cellLookupOptions () {
      const keyword = this.cellLookup.trim().toLowerCase()
      return this.cells.filter(cell => !keyword || [cell.id, cell.zone].join(' ').toLowerCase().includes(keyword)).slice(0, 50)
    }
  },
  watch: {
    selected (next, previous) {
      if (next?.id !== previous?.id) {
        this.cellLookup = ''; this.mapLookupError = ''; this.mapPanMode = false; this.mapZoom = 1
        this.$nextTick(() => this.resetMapView())
      }
      this.$nextTick(() => this.bindMapViewport())
    },
    appliedGridSize (next, previous) {
      if (!previous || ['rows', 'cols', 'width', 'height'].some(key => next?.[key] !== previous[key])) {
        this.cellLookup = ''; this.mapLookupError = ''; this.mapZoom = 1
        this.$nextTick(() => this.resetMapView())
      }
      this.$nextTick(() => this.bindMapViewport())
    },
    drawing (value) { if (value) this.mapPanMode = false },
    pointPicking (value) { if (value) this.mapPanMode = false },
    schemeSelecting (value) { if (value) this.mapPanMode = false }
  },
  mounted () {
    this.mapEscapeHandler = event => {
      if (event.key === 'Escape' && (this.sceneDrawingActive || this.sceneResizingActive) && !this.previewOpen && !this.leaveOpen && !event.target?.closest?.('[role="dialog"]')) { event.preventDefault(); this.stopSceneDrawing(); return }
      if (event.key === 'Escape' && this.mapExpanded && !this.previewOpen && !this.leaveOpen && !event.target?.closest?.('[role="dialog"]')) { event.preventDefault(); this.toggleMapExpanded() }
    }
    window.addEventListener('keydown', this.mapEscapeHandler)
    this.mapPanEndHandler = event => this.endMapPan(event)
    this.mapPanBlurHandler = () => this.endMapPan()
    window.addEventListener('pointerup', this.mapPanEndHandler)
    window.addEventListener('pointercancel', this.mapPanEndHandler)
    window.addEventListener('blur', this.mapPanBlurHandler)
    this.bindMapViewport()
  },
  beforeDestroy () {
    this.endMapPan()
    this.mapViewportObserver?.disconnect()
    window.removeEventListener('keydown', this.mapEscapeHandler)
    window.removeEventListener('pointerup', this.mapPanEndHandler)
    window.removeEventListener('pointercancel', this.mapPanEndHandler)
    window.removeEventListener('blur', this.mapPanBlurHandler)
    if (this.mapExpanded) document.body.style.overflow = this.mapPreviousBodyOverflow
  },
  methods: {
    bindMapViewport () {
      const viewport = this.$refs.mapViewport
      if (!viewport || this.observedMapViewport === viewport) { this.measureMapViewport(); return }
      this.mapViewportObserver?.disconnect(); this.observedMapViewport = viewport
      if (typeof ResizeObserver !== 'undefined') {
        this.mapViewportObserver = new ResizeObserver(() => this.measureMapViewport())
        this.mapViewportObserver.observe(viewport)
      }
      this.measureMapViewport()
    },
    measureMapViewport () {
      const viewport = this.$refs.mapViewport
      if (viewport) { this.mapViewportWidth = viewport.clientWidth; this.mapViewportHeight = viewport.clientHeight }
      if (this.isIsometricEditor) this.$nextTick(() => this.constrainIsometricPan())
    },
    cellBounds (cell) {
      const ring = cell.geometry?.coordinates?.[0]
      if (String(cell.geometry?.type).toUpperCase() === 'POLYGON' && Array.isArray(ring) && ring.length) {
        const xs = ring.map(p => Number(p[0])); const ys = ring.map(p => Number(p[1]))
        return { x: Math.min(...xs), y: Math.min(...ys), width: Math.max(...xs) - Math.min(...xs), height: Math.max(...ys) - Math.min(...ys) }
      }
      return { x: this.cellX(cell), y: this.cellY(cell), width: this.cellWidth(cell), height: this.cellHeight(cell) }
    },
    cellLabelVisible (cell) {
      if (!this.showCellLabels) return false
      if (cell.id === this.selectedCell?.id) return true
      const bounds = this.cellBounds(cell); const scale = this.mapViewportWidth * this.mapZoom / this.mapWidth
      return bounds.width * scale >= 40 && bounds.height * scale >= 20
    },
    cellZoneVisible (cell) {
      const bounds = this.cellBounds(cell); const scale = this.mapViewportWidth * this.mapZoom / this.mapWidth
      return bounds.width * scale >= 65 && bounds.height * scale >= 42
    },
    cellMapSummary (cell) { return cell.id + ' · ' + (cell.zone || '居住区') + ' · 人口 ' + cell.population + ' · 经济 ' + cell.economy + ' · 风险 ' + cell.risk },
    mapCenter () {
      if (this.isIsometricEditor) return this.$refs.isoEditor?.centerGround() || null
      const viewport = this.$refs.mapViewport; const svg = this.$refs.mapSvg
      if (!viewport || !svg || !svg.getScreenCTM()) return null
      const rect = viewport.getBoundingClientRect(); const point = svg.createSVGPoint()
      point.x = rect.left + viewport.clientWidth / 2; point.y = rect.top + viewport.clientHeight / 2
      const value = point.matrixTransform(svg.getScreenCTM().inverse())
      return { x: Math.max(0, Math.min(this.mapWidth, value.x)), y: Math.max(0, Math.min(this.mapHeight, value.y)) }
    },
    centerMapOn (target) {
      if (this.isIsometricEditor) { if (target) this.$refs.isoEditor?.centerOn(target); return }
      const viewport = this.$refs.mapViewport; const svg = this.$refs.mapSvg
      if (!target || !viewport || !svg?.getScreenCTM()) return
      const rect = viewport.getBoundingClientRect(); const point = svg.createSVGPoint()
      point.x = target.x; point.y = target.y
      const screen = point.matrixTransform(svg.getScreenCTM())
      viewport.scrollLeft += screen.x - rect.left - viewport.clientWidth / 2
      viewport.scrollTop += screen.y - rect.top - viewport.clientHeight / 2
    },
    changeMapZoom (step) {
      const center = this.mapCenter()
      this.mapZoom = Math.max(.1, Math.min(4, Number((this.mapZoom + step).toFixed(2))))
      this.$nextTick(() => this.centerMapOn(center))
    },
    resetMapView () {
      this.endMapPan(); this.mapPanX = 0; this.mapPanY = 0; this.mapPanBlockUntil = 0
      this.mapZoom = 1
      this.$nextTick(() => { this.measureMapViewport(); const viewport = this.$refs.mapViewport; if (viewport) { viewport.scrollLeft = 0; viewport.scrollTop = 0 } })
    },
    fitMapView () {
      this.measureMapViewport()
      if (!this.mapViewportWidth || !this.mapViewportHeight) return
      const width = this.isIsometricEditor ? this.$refs.isoEditor.frame.width : this.mapWidth, height = this.isIsometricEditor ? this.$refs.isoEditor.frame.height : this.mapHeight
      this.endMapPan(); this.mapPanX = 0; this.mapPanY = 0; this.mapPanBlockUntil = 0
      this.mapZoom = Math.min(1, this.mapViewportHeight * width / (this.mapViewportWidth * height))
      this.$nextTick(() => { const viewport = this.$refs.mapViewport; viewport.scrollLeft = 0; viewport.scrollTop = 0 })
    },
    locateMapCell () {
      const code = this.cellLookup.trim().toLowerCase()
      const cell = this.cells.find(cell => String(cell.id).toLowerCase() === code)
      if (!cell) { this.mapLookupError = '请输入有效单元编号，例如 ' + (this.cells[0]?.id || 'C0_0'); return }
      if (this.drawing || this.pointPicking || this.sceneDrawingActive) { this.mapLookupError = '请先完成或结束当前绘制/点选，再定位单元'; return }
      if (!this.checkPointApplied() || !this.checkSceneApplied()) return
      this.mapLookupError = ''; this.cellLookup = cell.id; this.selectCell(cell)
      if (!this.schemeSelecting) this.openEditorTab('cells')
      const bounds = this.cellBounds(cell)
      this.$nextTick(() => this.centerMapOn({ x: bounds.x + bounds.width / 2, y: bounds.y + bounds.height / 2 }))
    },
    showMapCellEditor () { this.mapConfigCollapsed = false; this.openEditorTab('cells'); this.$nextTick(() => this.measureMapViewport()) },
    toggleMapConfig () {
      const center = this.mapCenter()
      this.mapConfigCollapsed = !this.mapConfigCollapsed
      this.$nextTick(() => { this.measureMapViewport(); this.centerMapOn(center) })
    },
    toggleMapExpanded () {
      const center = this.mapCenter()
      if (!this.mapExpanded) { this.mapPreviousBodyOverflow = document.body.style.overflow; document.body.style.overflow = 'hidden' }
      else document.body.style.overflow = this.mapPreviousBodyOverflow
      this.mapExpanded = !this.mapExpanded
      this.$nextTick(() => { this.measureMapViewport(); this.centerMapOn(center); this.queueEditorMeasure() })
    },
    startMapPan (event) {
      if (event.button === 0 && event.isPrimary !== false) this.mapPanBlockUntil = 0
      if ((!this.mapPanMode && !this.isIsometricEditor) || event.button !== 0 || event.isPrimary === false || this.mapPanStart || this.sceneDrag || event.target?.closest?.('[data-scene-resize-handle]')) return
      const viewport = this.$refs.mapViewport, immediate = this.mapPanMode
      this.mapPanning = immediate
      this.mapPanStart = { id: event.pointerId, x: event.clientX, y: event.clientY, left: this.isIsometricEditor ? this.mapPanX : viewport.scrollLeft, top: this.isIsometricEditor ? this.mapPanY : viewport.scrollTop, moved: immediate }
      // Capture only after movement in the default 2.5D tool so a single click
      // still reaches its building, point, grid label or drawing location.
      if (immediate) { try { viewport.setPointerCapture?.(event.pointerId) } catch (_) {} event.preventDefault() }
    },
    moveMapPan (event) {
      if (this.mapPanStart?.id !== event.pointerId) return
      const start = this.mapPanStart; const viewport = this.$refs.mapViewport
      if (!start) return
      const dx = event.clientX - start.x, dy = event.clientY - start.y
      if (!start.moved && Math.hypot(dx, dy) < 4) return
      if (!start.moved) { start.moved = true; this.mapPanning = true; try { viewport.setPointerCapture?.(event.pointerId) } catch (_) {} }
      if (this.isIsometricEditor) { this.mapPanX = start.left + dx; this.mapPanY = start.top + dy; this.constrainIsometricPan() }
      else { viewport.scrollLeft = start.left - dx; viewport.scrollTop = start.top - dy }
      event.preventDefault()
    },
    endMapPan (event) {
      const start = this.mapPanStart
      if (!start || event && start.id !== event.pointerId) return
      if (start.moved) this.mapPanBlockUntil = Date.now() + 300
      this.mapPanning = false; this.mapPanStart = null
      const viewport = this.$refs.mapViewport
      try { if (viewport?.hasPointerCapture?.(start.id)) viewport.releasePointerCapture(start.id) } catch (_) {}
    },
    leaveMapPan () { if (this.mapPanStart && !this.mapPanStart.moved) this.endMapPan() },
    panIsometricBy (x, y) { this.mapPanX += x; this.mapPanY += y; this.constrainIsometricPan() },
    constrainIsometricPan () {
      if (!this.isIsometricEditor) return
      const viewport = this.$refs.mapViewport, svg = this.$refs.isoEditor?.$refs.svg
      if (!viewport || !svg) return
      const v = viewport.getBoundingClientRect(), r = svg.getBoundingClientRect()
      // Props describe the transform already rendered, while parent offsets may
      // have just changed and are still waiting for the next Vue render.
      const left = r.left - v.left - viewport.clientLeft - this.$refs.isoEditor.panX, top = r.top - v.top - viewport.clientTop - this.$refs.isoEditor.panY
      this.mapPanX = Math.max(Math.min(0, viewport.clientWidth * .2 - left - r.width), Math.min(Math.max(0, viewport.clientWidth * .8 - left), this.mapPanX))
      this.mapPanY = Math.max(Math.min(0, viewport.clientHeight * .2 - top - r.height), Math.min(Math.max(0, viewport.clientHeight * .8 - top), this.mapPanY))
    },
    blockPanClick (event) { if (this.mapPanMode || this.mapPanning || Date.now() < this.mapPanBlockUntil) { event.preventDefault(); event.stopPropagation() } }
  }
}
